import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MetricKey } from "../components/charts/common/utils";
import { buildMetricOptions, filterMetricsByPermissions, MetricOption } from "../components/utils/metrics";
import { PointDetailsPermissions } from "../types";

export const useMetricOptions = ({
  metric,
  setMetric,
  showTtip = false,
  isPointLevel = false,
  permissions,
  translationPrefix = "portfolios.portfolioLevel.assetRisk",
}: {
  metric: MetricKey;
  setMetric: (value: MetricKey) => void;
  showTtip?: boolean;
  /** Only changes how each option abbreviates — TTIP reads "TIP" against a single asset. */
  isPointLevel?: boolean;
  permissions?: PointDetailsPermissions;
  translationPrefix?: string;
}): { metricOptions: MetricOption[]; selectedMetric: MetricOption | undefined } => {
  const { t } = useTranslation("tools", { keyPrefix: translationPrefix });

  const metricOptions = useMemo(
    () =>
      filterMetricsByPermissions(
        buildMetricOptions((key) => t(key) ?? "", showTtip, isPointLevel),
        permissions
      ),
    [t, showTtip, isPointLevel, permissions]
  );

  // Ensure the selected metric is always present in the filtered list
  useEffect(() => {
    if (metricOptions.length > 0 && !metricOptions.find((o) => o.value === metric)) {
      setMetric(metricOptions[0].value);
    }
  }, [metricOptions, metric, setMetric]);

  const selectedMetric = metricOptions.find((o) => o.value === metric) ?? metricOptions[0];

  return { metricOptions, selectedMetric };
};
